import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import { IconAlertCircle, IconCheckCircle, IconInfo } from '../icons'

interface Toast { id: number; text: string; tone: 'info' | 'error' | 'success' }
const Ctx = createContext<(text: string, tone?: Toast['tone']) => void>(() => {})

const TONE_ICON = {
  error: { Icon: IconAlertCircle, className: 'text-red-600' },
  success: { Icon: IconCheckCircle, className: 'text-mint-500' },
  info: { Icon: IconInfo, className: 'text-ginger-500' }
} as const

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const push = useCallback((text: string, tone: Toast['tone'] = 'info') => {
    const id = Date.now() + Math.random()
    setToasts(t => [...t, { id, text, tone }])
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 5000)
  }, [])
  return (
    <Ctx.Provider value={push}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-4 bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] z-[60] flex flex-col items-center gap-2 sm:inset-x-auto sm:right-4 sm:w-96 sm:items-stretch">
        {toasts.map(t => {
          const { Icon, className } = TONE_ICON[t.tone]
          return (
            <div
              key={t.id}
              role={t.tone === 'error' ? 'alert' : 'status'}
              className="pointer-events-auto flex w-full items-start gap-2.5 rounded-xl border border-line bg-cream-50 px-4 py-3 text-[13.5px] text-ink-100 shadow-pop"
            >
              <Icon className={`mt-0.5 h-[18px] w-[18px] shrink-0 ${className}`} />
              <span className="min-w-0 flex-1 break-words">{t.text}</span>
            </div>
          )
        })}
      </div>
    </Ctx.Provider>
  )
}

export function useToast() {
  return useContext(Ctx)
}
